import { confirmPayment, handleNextAction, handleNextActionForSetup, initPaymentSheet, initStripe, PlatformPay, presentPaymentSheet, retrieveSetupIntent, usePlatformPay, } from 'utils/packages/stripe';
import { useCallback, useMemo, useState } from 'react';
import { addDays } from 'date-fns';
import { useAppDispatch, useAppSelector, useAppStore } from 'store/hooks';
import { getSyncingStatus } from 'actions/connections';
import Modal from 'utils/packages/Modal';
import { useFetchOld } from 'hooks';
import useColorScheme from 'hooks/useColorScheme';
import Config from 'utils/packages/configs';
import { capitalizeFirstCharacter, formatPlural } from 'utils';
import { getSharedPlans, purchaseExtraMemberSlots } from 'actions/sharedPlans';
import { getUser } from 'actions/user';
import { getStripeProduct } from 'features/promotions/entities';
import Sentry from 'utils/sentry';
import { cancelStripeSubscription, changeAchPaymentMethod, changeSubscriptionViaStripe, createStripeCustomer, getAchEligibleAccounts, getStripeCustomer, reactivateStripeSubscription, clearStripeFailedPayment, clearStripeSeatBasedFailedPayment, retryStripeSubscriptionPayment, setUpIntent, setUpIntentConfirm, subscribeViaStripe, } from '../actions/stripe';
import showAchAccountSelectorModal from '../components/AchAccountSelectorModal';
import { isPremiumTier, isPremiumTierPeriodUpgrade, Tier, tierGreaterThan, TierPeriod, } from '../entities';
import { selectAbApplePaySheet, selectAbPaymentConfirmationModal, selectShouldShowStripe, selectStripePlaidAch, } from '../selectors';
import { logPurchaseCompleteEvent, } from '../utils/analytics';
const subscriptionToCartItem = (subscription, amount) => ({
    label: `Emma ${capitalizeFirstCharacter(subscription.tier)} ${capitalizeFirstCharacter(subscription.period)}`,
    amount,
    paymentType: PlatformPay.PaymentType.Recurring,
    ...{
        intervalUnit: subscription.period === TierPeriod.monthly
            ? PlatformPay.IntervalUnit.Month
            : PlatformPay.IntervalUnit.Year,
        intervalCount: 1,
    },
});
/**
 * Apple requires a web page where the user can update or delete the
 * payment method. Emma manages subscriptions in-app only, so this
 * points at the help centre article describing that flow.
 */
const SUBSCRIPTION_MANAGEMENT_URL = 'https://help.emma-app.com/en/article/cancel-emma-plus-emma-pro-or-emma-ultimate-h97m7x/';
const subscriptionToRecurringRequest = (subscription, amount, trialDays, renewsOn) => ({
    type: PlatformPay.PaymentRequestType.Recurring,
    description: `Emma ${capitalizeFirstCharacter(subscription.tier)} ${capitalizeFirstCharacter(subscription.period)}`,
    managementUrl: SUBSCRIPTION_MANAGEMENT_URL,
    billingAgreement: 'Cancel at any time. T&Cs apply',
    billing: {
        ...subscriptionToCartItem(subscription, amount),
        startDate: renewsOn.getTime() / 1000,
    },
    trialBilling: {
        label: `Emma ${capitalizeFirstCharacter(subscription.tier)} Free Trial`,
        amount: '0.00',
        paymentType: PlatformPay.PaymentType.Recurring,
        intervalUnit: PlatformPay.IntervalUnit.Day,
        intervalCount: trialDays,
        startDate: new Date().getTime() / 1000,
        endDate: renewsOn.getTime() / 1000,
    },
});
const returnURL = 'emma://stripe-redirect';
const isGooglePayFailure = (error) => !!error?.message?.startsWith('Google Pay failed');
const useStripePayment = () => {
    const dispatch = useAppDispatch();
    const store = useAppStore();
    const canUseStripe = useAppSelector(selectShouldShowStripe);
    const stripePriceMap = useAppSelector((state) => state.stripe.priceMap);
    const stripeCustomer = useAppSelector((state) => state.stripe.customer);
    const guessedHomeCountry = useAppSelector((state) => state.user.user.guessedHomeCountry);
    const abPaymentConfirmationModal = useAppSelector(selectAbPaymentConfirmationModal);
    const showStripePlaidAch = useAppSelector(selectStripePlaidAch);
    const abApplePaySheet = useAppSelector(selectAbApplePaySheet);
    const FREE_TRIAL = useMemo(() => ({
        label: `Emma${abApplePaySheet ? '' : ' Trial - Cancel Anytime'}`,
        amount: '0.00',
        paymentType: PlatformPay.PaymentType.Immediate,
    }), [abApplePaySheet]);
    const userCurrency = guessedHomeCountry === 'US' ? 'USD' : 'GBP';
    const [, , fetching, fetch] = useFetchOld({
        muteError: true,
        muteResult: true,
    });
    const { confirmPlatformPaySetupIntent, confirmPlatformPayPayment, isPlatformPaySupported, } = usePlatformPay();
    const checkIfPlatformPaySupported = useCallback(async () => {
        try {
            await initStripe({
                stripeAccountId: Config.STRIPE_ACCOUNT_ID,
                merchantIdentifier: Config.STRIPE_MERCHANT_ID,
                publishableKey: Config.STRIPE_PUBLISHABLE_KEY ?? '',
            });
            return await isPlatformPaySupported({
                googlePay: {
                    testEnv: Config.STRIPE_ENV === 'TEST',
                    existingPaymentMethodRequired: true,
                },
            });
        }
        catch {
            return false;
        }
    }, [isPlatformPaySupported]);
    const getOrCreateStripeCustomer = useCallback(async () => {
        const stripeCustomerResp = await dispatch(getStripeCustomer());
        if (!stripeCustomerResp ||
            stripeCustomerResp?.errorMessage) {
            throw new Error('Error while fetching customer');
        }
        else if (stripeCustomerResp.payload.customer) {
            return stripeCustomerResp.payload.customer;
        }
        else {
            const createStripeCustomerResp = await dispatch(createStripeCustomer());
            if (createStripeCustomerResp?.payload?.customer) {
                return createStripeCustomerResp.payload.customer;
            }
            throw new Error('Error while creating customer');
        }
    }, []);
    const [isPurchasing, setIsPurchasing] = useState(false);
    const colorScheme = useColorScheme();
    const createNewIntent = useCallback(async (subscription, cartItems, stripeCustomerId, recurringRequest) => {
        let clientSecret;
        const setUpIntentResp = await dispatch(setUpIntent());
        if (setUpIntentResp?.payload?.clientSecret) {
            clientSecret = setUpIntentResp.payload.clientSecret;
        }
        else {
            throw new Error('Error while setting up intent');
        }
        if (!clientSecret) {
            throw new Error('No client secret');
        }
        let setupIntent;
        let googlePayFailed = false;
        const platformPaySupported = store.getState().stripe.isPlatformPaySupported;
        if (platformPaySupported) {
            const setupPlatformPayResponse = await confirmPlatformPaySetupIntent(clientSecret, {
                applePay: {
                    cartItems,
                    ...(recurringRequest && { request: recurringRequest }),
                    merchantCountryCode: 'GB',
                    currencyCode: subscription.currency || userCurrency,
                    shippingMethods: [],
                    requiredShippingAddressFields: [],
                    requiredBillingContactFields: [],
                },
                googlePay: {
                    testEnv: Config.STRIPE_ENV === 'TEST',
                    merchantName: `Emma ${capitalizeFirstCharacter(subscription.tier)} ${capitalizeFirstCharacter(subscription.period)}`,
                    merchantCountryCode: 'GB',
                    currencyCode: subscription.currency || userCurrency,
                    billingAddressConfig: {
                        isPhoneNumberRequired: false,
                        isRequired: false,
                    },
                    amount: Number(cartItems[cartItems.length - 1].amount) * 100,
                },
            });
            if (isGooglePayFailure(setupPlatformPayResponse.error)) {
                Sentry.logError({
                    filename: 'useStripePayment.ts',
                    err: setupPlatformPayResponse.error,
                });
                googlePayFailed = true;
            }
            else if (setupPlatformPayResponse.error?.code) {
                throw new Error(setupPlatformPayResponse.error.message);
            }
            else {
                const retrieveSetupIntentResponse = await retrieveSetupIntent(setUpIntentResp.payload.clientSecret);
                setupIntent = retrieveSetupIntentResponse.setupIntent;
            }
        }
        if (!platformPaySupported || googlePayFailed) {
            const customerId = stripeCustomerId || (await getOrCreateStripeCustomer()).id;
            if (!customerId) {
                throw new Error('Initialising payment sheet requires a customer id');
            }
            const { error: initPaymentSheetError } = await initPaymentSheet({
                customerId,
                merchantDisplayName: 'Emma',
                applePay: {
                    merchantCountryCode: 'GB',
                    cartItems,
                    ...(recurringRequest && { request: recurringRequest }),
                },
                ...(!googlePayFailed && {
                    googlePay: {
                        merchantCountryCode: 'GB',
                        currencyCode: subscription.currency,
                        testEnv: Config.STRIPE_ENV === 'TEST',
                    },
                }),
                setupIntentClientSecret: setUpIntentResp.payload.clientSecret,
                style: colorScheme === 'dark' ? 'alwaysDark' : 'alwaysLight',
                allowsDelayedPaymentMethods: false,
                returnURL: 'emma://stripe-redirect',
            });
            if (initPaymentSheetError?.code) {
                throw new Error(initPaymentSheetError.message);
            }
            const { error: paymentSheetError } = await presentPaymentSheet();
            if (paymentSheetError?.code) {
                throw new Error(paymentSheetError.message);
            }
            const retrieveSetupIntentResponse = await retrieveSetupIntent(setUpIntentResp.payload.clientSecret);
            setupIntent = retrieveSetupIntentResponse.setupIntent;
        }
        while (setupIntent?.status === 'RequiresAction' &&
            (!setupIntent.nextAction ||
                // Given that a mapping doesn’t exist, this useStripeSdk isn’t really possible,
                // but we just  leave it here in case an SDK upgrade ends up adding it..
                // the name useStripeSdk follows the notation that they use to map all other types so if they add it it’s likely to be this and still work
                setupIntent?.nextAction?.type === 'useStripeSdk')) {
            // eslint-disable-next-line no-await-in-loop
            const nextAction = await handleNextActionForSetup(clientSecret, returnURL);
            if (nextAction.error) {
                throw new Error(nextAction.error.localizedMessage ||
                    nextAction.error.message ||
                    'Error handling next action');
            }
            setupIntent = nextAction.setupIntent;
        }
        const paymentMethodId = setupIntent?.paymentMethod?.id ?? setupIntent?.paymentMethodId;
        if (!paymentMethodId) {
            throw new Error('Unable to retrieve payment method id');
        }
        await dispatch(setUpIntentConfirm(paymentMethodId));
    }, [
        colorScheme,
        confirmPlatformPaySetupIntent,
        getOrCreateStripeCustomer,
        userCurrency,
    ]);
    const handlePaymentIntentAction = useCallback(async (clientSecret, paymentIntentStatus, platformPayConfig) => {
        if (paymentIntentStatus === 'requires_action') {
            const result = await handleNextAction(clientSecret, returnURL);
            if (result.error) {
                throw new Error(result.error.localizedMessage ||
                    result.error.message ||
                    'Error handling payment action');
            }
            return result;
        }
        if (paymentIntentStatus === 'requires_confirmation') {
            const result = await confirmPayment(clientSecret);
            if (result.error) {
                throw new Error(result.error.localizedMessage ||
                    result.error.message ||
                    'Error confirming payment');
            }
            return result;
        }
        if (paymentIntentStatus === 'requires_payment_method') {
            if (store.getState().stripe.isPlatformPaySupported &&
                platformPayConfig) {
                const { error, paymentIntent } = await confirmPlatformPayPayment(clientSecret, {
                    applePay: {
                        cartItems: platformPayConfig.cartItems,
                        merchantCountryCode: 'GB',
                        currencyCode: platformPayConfig.currencyCode,
                    },
                    googlePay: {
                        testEnv: Config.STRIPE_ENV === 'TEST',
                        merchantName: platformPayConfig.merchantName,
                        merchantCountryCode: 'GB',
                        currencyCode: platformPayConfig.currencyCode,
                    },
                });
                if (!isGooglePayFailure(error)) {
                    if (error) {
                        throw new Error(error.localizedMessage || error.message || 'Payment failed');
                    }
                    if (paymentIntent?.status === 'RequiresAction') {
                        const result = await handleNextAction(clientSecret, returnURL);
                        if (result.error) {
                            throw new Error(result.error.localizedMessage ||
                                result.error.message ||
                                'Error handling payment action');
                        }
                    }
                    return null;
                }
                Sentry.logError({ filename: 'useStripePayment.ts', err: error });
            }
            const customerId = stripeCustomer?.id || (await getOrCreateStripeCustomer()).id;
            if (!customerId) {
                throw new Error('Initialising payment sheet requires a customer id');
            }
            const { error: initError } = await initPaymentSheet({
                customerId,
                merchantDisplayName: 'Emma',
                paymentIntentClientSecret: clientSecret,
                style: colorScheme === 'dark' ? 'alwaysDark' : 'alwaysLight',
                allowsDelayedPaymentMethods: false,
                returnURL,
            });
            if (initError?.code) {
                throw new Error(initError.message);
            }
            const { error: presentError } = await presentPaymentSheet();
            if (presentError?.code) {
                throw new Error(presentError.message);
            }
            return null;
        }
        return null;
    }, [
        colorScheme,
        confirmPlatformPayPayment,
        getOrCreateStripeCustomer,
        stripeCustomer?.id,
    ]);
    const promptAchAccountSelection = useCallback(async (payingImmediately) => {
        const resp = await dispatch(getAchEligibleAccounts());
        const accounts = resp?.payload?.eligibleAccounts ?? [];
        if (!accounts.length) {
            return { account: null, hasEligibleAccounts: false };
        }
        const account = await new Promise((resolve) => {
            let resolved = false;
            const cb = () => {
                showAchAccountSelectorModal({
                    accounts,
                    payingImmediately,
                    onContinue: async (selected) => {
                        resolved = true;
                        resolve(selected);
                    },
                    onDismissed: () => {
                        if (!resolved) {
                            resolved = true;
                            resolve(null);
                        }
                    },
                });
            };
            const isModalOpen = Modal.isAnyModalOpen?.();
            if (isModalOpen) {
                Modal.hide(cb);
            }
            else {
                cb();
            }
        });
        return { account, hasEligibleAccounts: true };
    }, []);
    const startStripeFlow = useCallback(async ({ doOnPurchase, analytics, subscription, emmaProStatus, discount, prorationDate, payToday, skipFreeTrial, isRefundFlow, }) => {
        setIsPurchasing(true);
        let proratedRefund;
        let achSelection = null;
        try {
            const stripeCustomer = await getOrCreateStripeCustomer();
            const userTier = emmaProStatus.tier ?? Tier.free;
            const period = emmaProStatus.period === 'yearly'
                ? TierPeriod.yearly
                : TierPeriod.monthly;
            const isUpgradeOrPeriodChange = tierGreaterThan(subscription.tier, userTier) ||
                (userTier !== Tier.free &&
                    period !== subscription.period &&
                    isPremiumTierPeriodUpgrade(subscription.tier, subscription.period, userTier, period));
            if ((!stripeCustomer?.invoice_settings?.default_payment_method &&
                !stripeCustomer.achPaymentMethod) ||
                (abPaymentConfirmationModal && isUpgradeOrPeriodChange)) {
                const setupCard = async () => {
                    const basePrice = subscription.price;
                    let amount = discount?.discount.price
                        ? discount.discount.price
                        : basePrice;
                    let startDate = new Date();
                    let trialDays = 0;
                    if (emmaProStatus.isOnFreeTrial &&
                        emmaProStatus.currentFreeTrialDays &&
                        emmaProStatus.expiresOn) {
                        trialDays = emmaProStatus.currentFreeTrialDays;
                        startDate = new Date(emmaProStatus.expiresOn);
                    }
                    else if (!skipFreeTrial && emmaProStatus.eligibleFreeTrialDays) {
                        trialDays = emmaProStatus.eligibleFreeTrialDays;
                        startDate = addDays(new Date(), emmaProStatus.eligibleFreeTrialDays);
                    }
                    else if (payToday !== undefined) {
                        amount = payToday.toFixed(2);
                    }
                    await createNewIntent(subscription, [
                        {
                            ...subscriptionToCartItem(subscription, amount),
                            ...(trialDays || amount === basePrice
                                ? { startDate: startDate.getTime() / 1000 }
                                : { paymentType: PlatformPay.PaymentType.Immediate }),
                        },
                        ...(trialDays ? [FREE_TRIAL] : []),
                    ], stripeCustomer.id || undefined, abApplePaySheet && trialDays
                        ? subscriptionToRecurringRequest(subscription, amount, trialDays, startDate)
                        : undefined);
                };
                if (showStripePlaidAch) {
                    const { account, hasEligibleAccounts } = await promptAchAccountSelection(!emmaProStatus.isOnFreeTrial &&
                        (skipFreeTrial || !emmaProStatus.eligibleFreeTrialDays));
                    if (!hasEligibleAccounts) {
                        await setupCard();
                    }
                    else if (!account) {
                        throw new Error('The payment flow has been canceled');
                    }
                    achSelection = account;
                }
                else {
                    await setupCard();
                }
            }
            const isChangingExistingSubscription = emmaProStatus?.active && emmaProStatus.store === 'stripe';
            if (isChangingExistingSubscription && achSelection) {
                const currentAch = stripeCustomer?.achPaymentMethod;
                const isDifferentAchAccount = currentAch?.plaidItemId !== achSelection.plaidItemId ||
                    currentAch?.plaidAccountId !== achSelection.plaidAccountId;
                if (isDifferentAchAccount) {
                    const patchResp = await dispatch(changeAchPaymentMethod({
                        plaidItemId: achSelection.plaidItemId,
                        plaidAccountId: achSelection.plaidAccountId,
                    }));
                    const errorMessage = patchResp?.errorMessage;
                    if (errorMessage) {
                        throw new Error(errorMessage);
                    }
                }
            }
            const subscriptionResp = isChangingExistingSubscription
                ? await dispatch(changeSubscriptionViaStripe(subscription.id, discount?.discount.couponId, prorationDate, isRefundFlow))
                : await dispatch(subscribeViaStripe(subscription.id, discount?.discount.couponId, skipFreeTrial, achSelection
                    ? {
                        plaidItemId: achSelection.plaidItemId,
                        plaidAccountId: achSelection.plaidAccountId,
                    }
                    : undefined));
            if (subscriptionResp?.errorMessage) {
                throw new Error(subscriptionResp?.errorMessage);
            }
            const payload = subscriptionResp?.payload;
            if (typeof payload === 'object' &&
                'clientSecret' in payload &&
                payload.clientSecret &&
                'paymentIntentStatus' in payload &&
                payload.paymentIntentStatus) {
                try {
                    await handlePaymentIntentAction(payload.clientSecret, payload.paymentIntentStatus);
                    dispatch(clearStripeFailedPayment());
                }
                catch (_e) {
                    // Payment action (e.g. 3DS) failed but the backend has already
                    // upgraded the subscription. The failed payment will surface
                    // via emmaProStatus.paymentFailed on next sync.
                }
            }
            if (typeof payload === 'object' && 'refundedAmount' in payload) {
                proratedRefund = {
                    refundedAmount: payload.refundedAmount ?? 0,
                    endingBalanceCredit: payload.endingBalanceCredit ?? 0,
                };
            }
            else {
                proratedRefund = undefined;
            }
        }
        finally {
            setIsPurchasing(false);
            Modal.hide();
        }
        if (achSelection) {
            dispatch(getStripeCustomer());
        }
        dispatch(getSyncingStatus());
        logPurchaseCompleteEvent(analytics, subscription.id, 'stripe', !skipFreeTrial && !!emmaProStatus.eligibleFreeTrialDays, discount?.discount.couponId);
        doOnPurchase?.(subscription.id, {
            id: subscription.id,
            period: subscription.period,
            tier: subscription.tier,
        }, discount, proratedRefund);
    }, [
        FREE_TRIAL,
        abApplePaySheet,
        abPaymentConfirmationModal,
        createNewIntent,
        getOrCreateStripeCustomer,
        handlePaymentIntentAction,
        promptAchAccountSelection,
        showStripePlaidAch,
    ]);
    const startSharedPlanPurchaseFlow = useCallback(async ({ plan, invoice, }) => {
        setIsPurchasing(true);
        try {
            const stripeCustomer = await getOrCreateStripeCustomer();
            if (!stripeCustomer?.invoice_settings?.default_payment_method) {
                const amount = invoice.payToday.amount.toFixed(2);
                const label = `Emma member ${formatPlural('slot', 'slots', invoice.priceForSlots.slots)}`;
                await createNewIntent({
                    currency: invoice.payToday.currency,
                    tier: Tier.ultimate,
                    period: TierPeriod.monthly,
                }, [
                    {
                        label,
                        amount,
                        paymentType: PlatformPay.PaymentType.Immediate,
                    },
                ], stripeCustomer?.id || undefined);
            }
            const purchaseResp = await dispatch(purchaseExtraMemberSlots({
                priceId: plan.priceId,
                numberOfSeats: plan.addSeats,
            }));
            if (purchaseResp?.errorMessage) {
                throw new Error(purchaseResp?.errorMessage);
            }
            const purchasePayload = purchaseResp?.payload;
            if (typeof purchasePayload === 'object' &&
                'clientSecret' in purchasePayload &&
                purchasePayload.clientSecret &&
                'paymentIntentStatus' in purchasePayload &&
                purchasePayload.paymentIntentStatus) {
                try {
                    // eslint-disable-next-line no-console
                    console.log('[Stripe] startSharedPlanPurchaseFlow: handling payment intent action');
                    await handlePaymentIntentAction(purchasePayload.clientSecret, purchasePayload.paymentIntentStatus);
                    dispatch(clearStripeSeatBasedFailedPayment());
                }
                catch (_e) {
                    // Payment action (e.g. 3DS) failed but the backend has already
                    // created the seat subscription. The failed payment will surface
                    // via extraMembersInfo.paymentFailed on next sync.
                }
            }
            dispatch(getSharedPlans());
        }
        finally {
            setIsPurchasing(false);
        }
    }, [createNewIntent, getOrCreateStripeCustomer, handlePaymentIntentAction]);
    const cancelStripeSubscriptionFn = useCallback(async () => {
        const result = await fetch(cancelStripeSubscription());
        if (result?.premiumSubscriptionStatus) {
            return true;
        }
        return false;
    }, []);
    const reactivateStripeSubscriptionFn = useCallback(async () => {
        const result = await fetch(reactivateStripeSubscription());
        if (result?.premiumSubscriptionStatus) {
            return true;
        }
        return false;
    }, []);
    const [changingPaymentMethod, setChangingPaymentMethod] = useState(false);
    const changePaymentMethod = useCallback(async (emmaProStatus, options) => {
        const isInEmployerPlan = 'type' in emmaProStatus &&
            emmaProStatus.type &&
            emmaProStatus.type === 'employee';
        const showChangePaymentMethod = emmaProStatus.store === 'stripe' &&
            isPremiumTier(emmaProStatus.tier) &&
            emmaProStatus.period &&
            emmaProStatus.productId;
        const showEmployerChangePaymentMethod = isInEmployerPlan && canUseStripe;
        if (!showChangePaymentMethod && !showEmployerChangePaymentMethod) {
            return;
        }
        const isSeatBased = options?.seatBased;
        const currentExtraMembersInfo = store.getState().sharedPlans.extraMembersInfo;
        const activePaymentFailed = isSeatBased
            ? currentExtraMembersInfo?.paymentFailed
            : emmaProStatus.paymentFailed;
        if (activePaymentFailed) {
            try {
                setChangingPaymentMethod(true);
                const resp = await dispatch(retryStripeSubscriptionPayment(isSeatBased ? { seatBased: true } : undefined));
                if (resp?.errorMessage) {
                    throw new Error(resp?.errorMessage);
                }
                const retryPayload = resp?.payload;
                if (retryPayload?.clientSecret && retryPayload?.paymentIntentStatus) {
                    const tier = emmaProStatus.tier;
                    const failedAmount = isSeatBased
                        ? currentExtraMembersInfo?.failedPaymentAmount
                        : emmaProStatus.failedPaymentAmount;
                    await handlePaymentIntentAction(retryPayload.clientSecret, retryPayload.paymentIntentStatus, failedAmount
                        ? {
                            cartItems: [
                                {
                                    label: `Emma ${capitalizeFirstCharacter(tier)}`,
                                    amount: failedAmount.amount.toFixed(2),
                                    paymentType: PlatformPay.PaymentType.Immediate,
                                },
                            ],
                            currencyCode: failedAmount.currency,
                            merchantName: `Emma ${capitalizeFirstCharacter(tier)}`,
                        }
                        : undefined);
                }
                if (isSeatBased) {
                    dispatch(clearStripeSeatBasedFailedPayment());
                }
                else {
                    dispatch(clearStripeFailedPayment());
                }
                dispatch(getUser());
                dispatch(getSharedPlans());
            }
            catch (e) {
                if (e.message !== 'The payment has been canceled' &&
                    e.message !== 'Google Pay has been canceled' &&
                    e.message !== 'The payment flow has been canceled') {
                    Modal.showError(e.message);
                }
            }
            finally {
                setChangingPaymentMethod(false);
            }
            return;
        }
        try {
            const tier = emmaProStatus.tier;
            const period = emmaProStatus.period === 'monthly'
                ? TierPeriod.monthly
                : TierPeriod.yearly;
            const price = showEmployerChangePaymentMethod
                ? { amount: 0, currency: userCurrency }
                : emmaProStatus.autoRenewPrice || emmaProStatus.currentPrice;
            const product = stripePriceMap && emmaProStatus.productId
                ? getStripeProduct(stripePriceMap, emmaProStatus.autoRenewProductId || emmaProStatus.productId)
                : undefined;
            setChangingPaymentMethod(true);
            await createNewIntent({
                currency: price?.currency || userCurrency,
                tier: product?.tier || tier,
                period: product?.period || period,
            }, [
                {
                    ...subscriptionToCartItem({
                        tier: product?.tier || tier,
                        period: product?.period || period,
                    }, price ? price.amount.toFixed(2) : '0.00'),
                    startDate: emmaProStatus.expiresOn && !showEmployerChangePaymentMethod
                        ? new Date(emmaProStatus.expiresOn).getTime() / 1000
                        : undefined,
                },
            ], stripeCustomer?.id || undefined);
        }
        catch (e) {
            if (e.message !== 'The payment has been canceled' &&
                e.message !== 'Google Pay has been canceled' &&
                e.message !== 'The payment flow has been canceled') {
                Modal.showError(e.message);
            }
        }
        finally {
            setChangingPaymentMethod(false);
            dispatch(getStripeCustomer());
        }
    }, [
        canUseStripe,
        createNewIntent,
        handlePaymentIntentAction,
        stripePriceMap,
        stripeCustomer?.id,
        userCurrency,
    ]);
    return {
        fetching: fetching || isPurchasing,
        changingPaymentMethod,
        canUseStripe,
        stripePriceMap,
        stripeCustomer,
        startStripeFlow,
        checkIfPlatformPaySupported,
        cancelStripeSubscriptionFn,
        reactivateStripeSubscriptionFn,
        changePaymentMethod,
        startSharedPlanPurchaseFlow,
        handlePaymentIntentAction,
    };
};
export default useStripePayment;
